'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
var _BucketConfig = require('./BucketConfig');
var _BucketConfig2 = _interopRequireDefault(_BucketConfig);
var _EvaluationData = require('./EvaluationData');
var _EvaluationData2 = _interopRequireDefault(_EvaluationData);
var _ExperimentEvaluator = require('./ExperimentEvaluator');
var _ExperimentEvaluator2 = _interopRequireDefault(_ExperimentEvaluator);
var _NoopExperimentEvaluator = require('./NoopExperimentEvaluator');
var _NoopExperimentEvaluator2 = _interopRequireDefault(_NoopExperimentEvaluator);
var _ExposureQueue = require('./ExposureQueue');
var _ExposureQueue2 = _interopRequireDefault(_ExposureQueue);
var _EgspExposureLogger = require('../ExposureLogger/EgspExposureLogger');
var _EgspExposureLogger2 = _interopRequireDefault(_EgspExposureLogger);
function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    default: obj
  };
}
var NOOP_EXPERIMENT_EVALUATOR = new _NoopExperimentEvaluator2.default();

/* eslint no-use-before-define: ["error", { "functions": false }]*/

/**
 * Initialize a global `__TNL__` object with the tests passed into the constructor as BucketConfig
 * The reason a global variable is used is so that shared components, which also
 * use @expediagroup/egtnl-bucket-web (but perhaps different versions), all reference
 * the same dictionary of tests. Cast these test objects as {@link EvaluationData} objects.
 *
 * The preferred way to evaluate and log exposures for an experiment is to use the API for `@expediagroup/egtnl-bucket-web`.
 * If you need to evaluate and log exposure before EgTnlBucket is initialized, like in a script tag printed to the page,
 * you can use `__TNL_QUEUE__.push()`.
 * See https://github.expedia.biz/EGExperimentation/abacus-client-utils#usage for details.
 *
 * @param {BucketConfig} config - bucket configuration for initialization
 */
// eslint-disable-next-line complexity
function initialize(config) {
  if (typeof window === 'undefined') {
    return;
  }
  var validationError = validateBucketConfig(config);
  if (validationError) {
    throw validationError;
  }
  if (evaluator() && !(evaluator() instanceof _NoopExperimentEvaluator2.default)) {
    return; // only initialize once
  }
  var cleanedEvaluatedExperiments = cleanEvaluatedExperiments(config.evaluatedExperiments);
  var loggers = config.exposureLoggers || [];
  var cleanedConfig = new _BucketConfig2.default(cleanedEvaluatedExperiments, loggers);
  var experimentEvaluator = new _ExperimentEvaluator2.default(cleanedConfig);
  setEvaluator(experimentEvaluator);

  // Save any experiments queued for exposure logging
  var exposuresToLog = Array.isArray(getExposureQueue()) ? getExposureQueue() : [];
  setExposureQueue(new _ExposureQueue2.default(experimentEvaluator));

  // Log queued exposures
  exposuresToLog.forEach(function (experimentId) {
    return experimentEvaluator.evaluateExperimentAndLog(experimentId);
  });
}

// eslint-disable-next-line consistent-return
function validateBucketConfig(config) {
  if (!(config instanceof _BucketConfig2.default)) {
    return new Error('Must pass a BucketConfig as part of initialize');
  }
  if (!config.evaluatedExperiments) {
    return new Error('BucketConfig evaluatedExperiments is not defined');
  }
}
function cleanEvaluatedExperiments(evaluatedExperiments) {
  // normalize EvaluationData for the evaluatedExperiments and throw error if any experiment key is not a number to guard
  //  against old experiment data where we were using strings as keys

  var experimentKeys = Object.keys(evaluatedExperiments);
  var cleanedEvaluatedExperiments = {};
  for (var i = 0; i < experimentKeys.length; ++i) {
    var experimentKey = experimentKeys[i];
    var experimentId = Number(experimentKey);
    if (Number.isNaN(experimentId)) {
      throw new Error('Experiment key must be a number: ' + experimentKey);
    }
    cleanedEvaluatedExperiments[experimentId] = new _EvaluationData2.default(evaluatedExperiments[experimentId]);
  }
  return cleanedEvaluatedExperiments;
}
function getExposureQueue() {
  return window.__TNL_QUEUE__;
}
function setExposureQueue(exposureQueue) {
  Object.defineProperty(window, '__TNL_QUEUE__', {
    writable: false,
    value: exposureQueue
  });
  Object.freeze(exposureQueue);
}
function setEvaluator(experimentEvaluator) {
  Object.defineProperty(window, '__TNL__', {
    writable: false,
    value: experimentEvaluator
  });
  Object.freeze(experimentEvaluator);
}
function evaluator() {
  if (typeof window === 'undefined' || !window.__TNL__) {
    return NOOP_EXPERIMENT_EVALUATOR;
  }
  return window.__TNL__;
}
function bucketConfig(evaluatedExperiments, exposureLoggers) {
  return new _BucketConfig2.default(evaluatedExperiments, exposureLoggers);
}
/**
 * @description receives an optional object of callbacks and returns a new instance of EgspExposureLogger, these callbacks can be used to handle errors when eg-clickstream fails for different reasons like fetch, invalid payload or an unexpected error during publish.
 *  If no callbacks are provided those will be NOOP functions under eg-clickstream-sdk-js instance.
 * @param {object} callbacks (optional) contains callbacks to handle errors during egclickstream execution, it contains
 * @param {function} callbacks.onFetchError callback to be executed when fetch fails.
 * @param {function} callbacks.onInvalidPayload callback to be executed when payload sent to publish function is not valid.
 * @param {function} callbacks.onPublishError callback to be executed when publish function throw any error.
 * @returns {EgspExposureLogger} new instance of EgspExposureLogger
 */
function egLogger(callbacks) {
  return new _EgspExposureLogger2.default(null, callbacks);
}
var EgTnlBucket = {
  initialize: initialize,
  evaluator: evaluator,
  bucketConfig: bucketConfig,
  egLogger: egLogger
};
exports.default = EgTnlBucket;